import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import Head from 'next/head'
import { AuthServices, UserServices, PaymentServices } from 'services'
import Cookies from 'js-cookie'
import { loginSuccess, setAccessToken } from 'actions/index'
import { ChevronLeft } from 'assets/icons/components'
import Link from 'next/link'
import { toast } from 'components/common/Toast'
import { LoadingOverlay } from 'components/common'
import config from 'config'
import { GoogleLogin, PublicLanguage } from 'components/common/index'
import nextI18NextConfig from '../../i18n'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { connect } from 'react-redux'
import axios from 'axios'
import styled, { keyframes, createGlobalStyle } from 'styled-components'

const HERO_IMAGE = '/images/main-bg.jpg'
const BLUE = '#4189dc'
const PINK = '#f84f7f'
const CARD_BG = '#fdfdfd'
const MOBILE = '@media (max-width: 768px)'
const MOBILE_SHORT = '@media (max-width: 768px) and (max-height: 724px)'

// iOS Safari counts the address-bar area in 100vh, so a full-height element
// overflows the visible viewport and triggers a scrollbar. dvh tracks the
// actual visible viewport; the plain vh line stays as a fallback for browsers
// that don't support dvh.
const FULL_HEIGHT = `
  min-height: 100vh;
  min-height: 100dvh;
`

const HideScrollbar = createGlobalStyle`
  ${MOBILE} {
    html,
    body {
      scrollbar-width: none;
      -ms-overflow-style: none;
    }
    html::-webkit-scrollbar,
    body::-webkit-scrollbar {
      width: 0;
      height: 0;
      display: none;
    }
  }
`

const Page = styled.div`
  width: 100%;
  ${FULL_HEIGHT}
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px;
  background: linear-gradient(135deg, #fde7ee 0%, #fbd6e1 100%);
  ${MOBILE} {
    padding: 0;
  }
`

const Frame = styled.div`
  border-radius: 36px;
  background: linear-gradient(150deg, #fcdbe6 0%, #f9cdda 100%);
  box-shadow: 0 30px 70px rgba(248, 79, 127, 0.2);
  ${MOBILE} {
    padding: 0;
    border-radius: 0;
    box-shadow: none;
    width: 100%;
    ${FULL_HEIGHT}
    display: flex;
  }
`

const Card = styled.div`
  position: relative;
  display: flex;
  width: 980px;
  max-width: calc(100vw - 68px);
  min-height: 620px;
  border-radius: 26px;
  overflow: hidden;
  background: ${CARD_BG};
  ${MOBILE} {
    display: block;
    width: 100%;
    max-width: none;
    ${FULL_HEIGHT}
    border-radius: 0;
  }
`

const Content = styled.div`
  position: relative;
  z-index: 1;
  flex: 0.92;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 56px 56px;
  ${MOBILE} {
    justify-content: flex-start;
    align-items: center;
    ${FULL_HEIGHT}
    padding: 72px 24px 28px;
  }
`

const Heading = styled.div`
  ${MOBILE} {
    align-self: flex-start;
  }
`

const Title = styled.h1`
  margin: 0;
  font-size: 38px;
  font-weight: 600;
  line-height: 1.05;
  letter-spacing: -0.5px;
  color: #1f1f1f;
  ${MOBILE} {
    font-size: 28px;
  }
`

const Subtitle = styled.p`
  margin: 16px 0 0;
  max-width: 300px;
  font-size: 17px;
  font-weight: 400;
  line-height: 1.45;
  color: #9a9aa2;
  text-align: center;
  ${MOBILE} {
    font-size: 15px;
    max-width: 240px;
    text-align: left;
  }
`

const Controls = styled.div`
  margin-top: 46px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  ${MOBILE} {
    margin-top: 160px;
    width: 100%;
  }
  ${MOBILE_SHORT} {
    margin-top: 96px;
  }
`

const GenderRow = styled.div`
  display: flex;
  gap: 28px;
  ${MOBILE} {
    gap: 18px;
  }
`

const GenderCard = styled.button`
  width: 158px;
  padding: 28px 0 22px;
  border: 2px solid
    ${(p) => (p.selected ? (p.variant === 'female' ? PINK : BLUE) : 'transparent')};
  border-radius: 20px;
  background: ${(p) =>
    p.selected ? (p.variant === 'female' ? '#fef4f7' : '#f3f8fe') : '#ffffff'};
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease,
    background 0.18s ease;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.12);
  }
  ${MOBILE} {
    width: 124px;
    padding: 20px 0 16px;
  }
`

const IconCircle = styled.div`
  width: 104px;
  height: 104px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => (p.variant === 'female' ? PINK : BLUE)};
  background: ${(p) => (p.variant === 'female' ? '#fde7ee' : '#e3eefb')};
  svg {
    width: 52px;
    height: 52px;
  }
  ${MOBILE} {
    width: 74px;
    height: 74px;
    svg {
      width: 40px;
      height: 40px;
    }
  }
`

const CardLabel = styled.span`
  margin-top: 18px;
  font-size: 17px;
  font-weight: 600;
  color: #222;
  ${MOBILE} {
    margin-top: 12px;
    font-size: 15px;
  }
`

const Underline = styled.span`
  margin-top: 10px;
  width: 40px;
  height: 3px;
  border-radius: 3px;
  background: ${(p) => (p.variant === 'female' ? PINK : BLUE)};
`

const appear = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`

const ContinueButton = styled.button`
  position: relative;
  animation: ${appear} 0.34s cubic-bezier(0.22, 1, 0.36, 1);
  margin-top: 30px;
  width: 100%;
  padding: 18px 26px;
  border: none;
  border-radius: 16px;
  background: ${BLUE};
  color: #ffffff;
  font-size: 17px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 16px 34px rgba(65, 137, 220, 0.32);
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease,
    opacity 0.18s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 20px 40px rgba(65, 137, 220, 0.42);
  }
  &:disabled {
    background: #c4d4ea;
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }
  svg {
    position: absolute;
    right: 24px;
    width: 22px;
    height: 22px;
  }
  ${MOBILE} {
    font-size: 16px;
    padding: 17px 24px;
    margin-top: 22px;
    width: calc(2 * 124px + 18px);
    max-width: 100%;
  }
`

const Divider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
  margin-top: 30px;
  color: #b4b4bd;
  font-size: 14px;
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e0e0e6;
  }
`

const LoginCard = styled.div`
  margin-top: 26px;
  padding: 17px 40px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.08);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 17px;
  font-weight: 600;
  z-index: 1;
  a {
    color: ${PINK};
    text-decoration: none;
    &:hover {
      opacity: 0.7;
    }
  }
  span {
    color: #c9c9d0;
    font-weight: 500;
  }
  ${MOBILE} {
    font-size: 16px;
    padding: 15px 34px;
  }
`

const Visual = styled.div`
  position: relative;
  flex: 1.1;
  overflow: hidden;
  background-color: ${CARD_BG};
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url(${HERO_IMAGE});
    background-size: contain;
    background-position: right center;
    background-repeat: no-repeat;
  }
  ${MOBILE} {
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 560px;
    flex: none;
    background-color: transparent;
    &::before {
      background-size: cover;
      background-position: right top;
      -webkit-mask-image: linear-gradient(to bottom, #000 68%, transparent 100%),
        linear-gradient(to right, transparent 0%, #000 44%);
      -webkit-mask-composite: source-in;
      mask-image: linear-gradient(to bottom, #000 68%, transparent 100%),
        linear-gradient(to right, transparent 0%, #000 44%);
      mask-composite: intersect;
    }
  }
`

const BackButton = styled.a`
  position: absolute;
  top: 28px;
  left: 28px;
  z-index: 6;
  display: flex;
  font-size: 28px;
  color: #1f1f1f;
  cursor: pointer;
  &:hover {
    opacity: 0.55;
  }
`

const LangPill = styled.div`
  position: absolute;
  top: 24px;
  right: 26px;
  z-index: 6;
  padding: 10px 18px;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.08);
`

const Wave = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 0;
  width: 66%;
  max-width: 410px;
  line-height: 0;
  pointer-events: none;
  -webkit-mask-image: linear-gradient(to right, #000 58%, transparent 100%);
  mask-image: linear-gradient(to right, #000 58%, transparent 100%);
  svg {
    width: 100%;
    height: auto;
    display: block;
  }
  ${MOBILE} {
    width: 100%;
    max-width: none;
  }
`

const MaleAvatar = (props) => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="3"
    strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="32" cy="25" r="10" />
    <path d="M22.5 20.5C24.5 13 39.5 13 41.5 20.5" />
    <path d="M15 51c0-9.4 7.6-17 17-17s17 7.6 17 17" />
  </svg>
)

const FemaleAvatar = (props) => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="3"
    strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="32" cy="26" r="8.5" />
    <path d="M21.5 27C21 16 26 11.5 32 11.5C38 11.5 43 16 42.5 27" />
    <path d="M21.5 27C21.5 34 23 39 24.8 42.5" />
    <path d="M42.5 27C42.5 34 41 39 39.2 42.5" />
    <path d="M17.5 51.5C17.5 43.5 24 38.5 32 38.5C40 38.5 46.5 43.5 46.5 51.5" />
  </svg>
)

const ArrowRight = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M4 12h15" />
    <path d="m13 6 6 6-6 6" />
  </svg>
)

const WaveShape = () => (
  <svg viewBox="0 0 600 240" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="wvA" x1="0" y1="0" x2="0.3" y2="1">
        <stop offset="0%" stopColor="#fbe3eb" />
        <stop offset="100%" stopColor="#f6d2de" />
      </linearGradient>
      <linearGradient id="wvB" x1="0" y1="0" x2="0.3" y2="1">
        <stop offset="0%" stopColor="#f5cfdc" />
        <stop offset="100%" stopColor="#efbbcd" />
      </linearGradient>
      <linearGradient id="wvC" x1="0" y1="0" x2="0.3" y2="1">
        <stop offset="0%" stopColor="#eebccd" />
        <stop offset="100%" stopColor="#e7a9bf" />
      </linearGradient>
      <filter id="wvSoft" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="1.6" />
      </filter>
    </defs>
    <path d="M0,96 C90,70 175,74 265,110 C375,154 475,184 600,198 L600,240 L0,240 Z"
      fill="url(#wvA)" opacity="0.85" filter="url(#wvSoft)" />
    <path d="M0,128 C80,104 165,106 255,140 C375,184 480,208 600,218 L600,240 L0,240 Z"
      fill="url(#wvB)" opacity="0.9" filter="url(#wvSoft)" />
    <path d="M0,160 C95,140 180,142 270,170 C390,206 490,222 600,228 L600,240 L0,240 Z"
      fill="url(#wvC)" opacity="0.95" />
  </svg>
)

const Trial = (props) => {
  const { dispatch, accessToken, locale } = props
  const { t } = useTranslation('trial')
  const [gender, setGender] = useState(null)
  const [isPageLoad, setIsPageLoad] = useState(false)
  const [isIframe, setIsIframe] = useState(false)
  const router = useRouter()

  useEffect(() => {
    const queries = new URLSearchParams(location.search)
    const ref = queries.get('ref') || queries.get('m')

    if (ref) {
      if (!Cookies.get('ref')) {
        PaymentServices.clickLink({
          ref: ref,
          domain: queries.get('domain')
        })
      }

      axios.defaults.headers.common['Ref'] = ref
      axios.defaults.headers.common['Ref-Pi'] = queries.get('pi')
      axios.defaults.headers.common['Ref-Domain'] = queries.get('domain')
      Cookies.set('ref', ref, { expires: 7, sameSite: 'lax' })
      Cookies.set('pi', queries.get('pi'), { expires: 7, sameSite: 'lax' })
      Cookies.set('domain', queries.get('domain'), { expires: 7, sameSite: 'lax' })
    }

    const gclid = queries.get('gclid')
    if (gclid) {
      Cookies.set('gclid', gclid, { expires: 7, sameSite: 'lax' })
    }

    setIsIframe(window.self !== window.top)

    getCurrentUser()
  }, [])

  const getCurrentUser = async () => {
    if (Cookies.get('accessToken')) {
      dispatch(setAccessToken(Cookies.get('accessToken')))
      setIsPageLoad(true)
      const { user, error } = await UserServices.getProfile() || {}
      setIsPageLoad(false)

      if (user) {
        dispatch(loginSuccess(user, accessToken))
        if (user?.gender_verified) {
          router.push('/live' + location.search)
        } else {
          router.push('/verify-gender')
        }
      } else {
        Cookies.remove('accessToken')
      }
    } else {
      setIsPageLoad(false)
    }
  }

  const handleSubmit = async (gender) => {
    setIsPageLoad(true)
    let { token, error } = (await AuthServices.guest({ gender: gender, language: locale })) || {}

    if (error || !token) {
      toast.error(error?.message || 'Something went wrong. Please try again.')
    } else {
      dispatch(setAccessToken(token))
      Cookies.set('accessToken', token, { expires: 30 })
      Cookies.get('gclid') && window.gtag && gtag('event', 'conversion', { send_to: 'AW-17596970317/VhqCCPXY48ocEM3q8cZB' })
      router.push('/live' + location.search)
    }

    setIsPageLoad(false)
  }

  const handleChooseGender = (selectedGender) => {
    setGender(selectedGender)
  }

  const handleContinue = () => {
    if (gender === null) return

    handleSubmit(gender)
  }

  const handleSocialLogin = async (provider, accessToken) => {
    if (accessToken) {
      const { token, error } = await AuthServices.socialGoogle({ provider: provider, ...accessToken })

      if (token) {
        dispatch(setAccessToken(token))
        Cookies.set('accessToken', token, { expires: 7 })
        router.push('/live')
      }
    }
  }

  if (isPageLoad) {
    return <LoadingOverlay />
  }

  return (
    <Page>
      <HideScrollbar />
      <Head>
        <title>Connectmingle | Free Trial</title>
        <meta name='description' content={t('description')} />
        <meta property='og:url' content={ config.URL + router.route } />
        <meta property='og:title' content='Connectmingle | Free Trial' />
        <meta property='og:image' content={ config.URL + '/images/logo.png' } />
        <meta property='og:description' content={t('description')} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Connectmingle" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content='Connectmingle | Free Trial' />
        <meta name="twitter:description" content={t('description')} />
        <meta name="twitter:image" content={ config.URL + '/images/logo.png' } />
        <meta name="twitter:url" content={ config.URL + router.route } />
        <meta name="robots" content="noindex" />
      </Head>

      <Frame>
        <Card>
          { !isIframe &&
            <Link href='/' passHref>
              <BackButton>
                <ChevronLeft />
              </BackButton>
            </Link>
          }

          <LangPill>
            <PublicLanguage t={t} locale={locale} path="/trial" />
          </LangPill>

          <Content>
            <Wave>
              <WaveShape />
            </Wave>

            <Heading>
              <Title>{t('You are')}...</Title>
              <Subtitle>{t('This helps us personalize your experience.')}</Subtitle>
            </Heading>

            <Controls>
              <GenderRow>
                <GenderCard
                  type='button'
                  variant='male'
                  selected={gender === 0}
                  onClick={() => handleChooseGender(0)}
                >
                  <IconCircle variant='male'>
                    <MaleAvatar />
                  </IconCircle>
                  <CardLabel>{t('Male')}</CardLabel>
                  <Underline variant='male' />
                </GenderCard>

                <GenderCard
                  type='button'
                  variant='female'
                  selected={gender === 1}
                  onClick={() => handleChooseGender(1)}
                >
                  <IconCircle variant='female'>
                    <FemaleAvatar />
                  </IconCircle>
                  <CardLabel>{t('Female')}</CardLabel>
                  <Underline variant='female' />
                </GenderCard>
              </GenderRow>

              {gender !== null && (
                <ContinueButton type='button' onClick={handleContinue}>
                  {t('Continue')}
                  <ArrowRight />
                </ContinueButton>
              )}

              <Divider>{t('or')}</Divider>

              <LoginCard>
                <Link href='/login'><a>{t('Login')}</a></Link>
                <span>/</span>
                <Link href='/register'><a>{t('Register')}</a></Link>
              </LoginCard>

              <GoogleLogin useOneTap={true} onSuccess={(accessToken) => handleSocialLogin('google', accessToken)} />
            </Controls>
          </Content>

          <Visual />
        </Card>
      </Frame>
    </Page>
  )
}

export const getStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale , ['common', 'trial'], nextI18NextConfig)),
    locale: locale
  }
})

const mapStateToProps = (state) => ({
  accessToken: state.connectionReducer.accessToken,
})

export default connect(mapStateToProps)(Trial)
